/**
 * universeLayout — derives the orbiting-fact stage layout from a real
 * UniverseGraph. Pure, presentation-only geometry (no React, no fetches).
 *
 * Rings are the brand's *display SUBJECTS* (see universeTaxonomy), not raw system
 * concepts. Six of the eight concepts are facets of the brand itself, so rendering
 * them as eight peer rings made "Voice" look like the same order of thing as
 * "Competitors". One subject = one orbit lane; its facets' facts ride that lane.
 *
 * Subjects with no backing concept ("planned", e.g. Category and Culture) never
 * become rings — an empty circle communicates nothing. They are kept in the
 * outline by the rail instead, as a stated intent.
 */
import type {
  UniverseGraph,
  UniverseFactNode,
} from "@/types/brandUniverse.type";
import {
  DEFAULT_SUBJECT_KEY,
  OTHER_SUBJECT_KEY,
  resolveTaxonomy,
  ringSubjects,
  UNSORTED_KEY,
  type ResolvedSubject,
} from "./universeTaxonomy";
import { buildUniverseOutline, type UniverseOutlineGroup } from "./universeOutline";

/** A fact laid out as an orbiting planet (presentation only). */
export type StageFact = {
  id: string;
  /** "new" = learned within NEW_WINDOW_DAYS (lit accent); else "established". */
  kind: "new" | "established";
  label: string;
  /** Display-lane FACET label (e.g. "Voice & tone"). "" when off-ring.
   *  Kept as `concept` so every existing printer of `f.concept` keeps compiling. */
  concept: string;
  /** Taxonomy keys for the display lane. "" when off-ring. */
  facetKey: string;
  subjectKey: string;
  /** Named competitor when the API can attribute the fact. */
  entityId: string | null;
  entityName: string;
  /** Breadcrumb for the detail card, e.g. "Brand · Voice & tone". */
  path: string;
  /**
   * EVERY ring concept this fact belongs to. A fact is genuinely many-to-many with
   * concepts (the backend's BrandUniverseFactConcept is a join table keyed on
   * [factId, conceptId], and the classifier attaches up to 3), so anything COUNTING
   * facts per concept must read this — never `ring`, which is one lane by design.
   */
  conceptIds: string[];
  desc: string;
  learnedAt: string;
  /** The graph's updatedAt, for the "Updated" date on the fact card. */
  updatedAt: string;
  /** Key of the ring arc this fact is placed in (its PRIMARY group, the same key
   *  the outline list uses). "" when the fact is off-lane. */
  groupKey: string;
  /** Other SUBJECTS this fact is also filed under (by a concept only that subject
   *  owns). It lists there too and echoes on that subject's ring. Only facts no
   *  marker routed can cross: a rival's fact tagged Channels is still a rival's. */
  crossSubjectKeys: string[];
  /** Display lane only: concept index → orbit ring. -1 when no orbit owns it. */
  ring: number;
  ang0: number;
  R: number;
  speed: number;
  yoff: number;
};

/**
 * One orbit lane = one display SUBJECT.
 *
 * `id` is a taxonomy subject key ("brand"), NOT a concept id. Anything counting
 * facts against a ring must go through `conceptIds`; `f.conceptIds.includes(ring.id)`
 * is now always false and would silently zero every count.
 */
export type Ring = {
  id: string;
  radius: number;
  label: string;
  /** Every backend concept id this subject covers. The join key for counting. */
  conceptIds: string[];
  blurb: string;
  /** Drift speed of this lane (radians per second of orbit clock). The same
   *  number every planet on the lane carries, so the ring turns rigidly. */
  speed: number;
  /** The ring split into its groups, in the subject's facet order. */
  arcs: RingArc[];
  /** Second filings: a fact filed under another group of the same subject shows
   *  there as a hollow square. Not interactive and never counted. */
  echoes: RingEcho[];
};

/**
 * One group's span on a ring. Angles are ring-local radians, the same space as a
 * planet's `ang0`, so a ring rotated by its phase carries its arcs and planets
 * together.
 */
export type RingArc = {
  /** The outline group key, so the list, the card and the orbit agree. */
  key: string;
  /** Group label as the list prints it ("Mission & vision", "Overview"). */
  label: string;
  subjectKey: string;
  /** The rival bucket (outline key) for arcs on the Competitors ring. */
  entityKey?: string;
  /** The rival's name for arcs on the Competitors ring. */
  entityName?: string;
  /** The backend concept name a fact must carry to be filed here. Absent for a
   *  catch-all (a rival's Overview, General). What an agent prompt must say. */
  concept?: string;
  start: number;
  end: number;
  /** False for a group with no facts yet: drawn dashed. */
  charted: boolean;
  /** Counted as a group by the status sentence and the next move. Facet arcs
   *  are; a General catch-all and the Across-the-set bucket are not. */
  countable: boolean;
  /** Every fact filed in this group, in list order, echoes included. */
  factIds: string[];
};

export type RingEcho = { factId: string; ang0: number; arcKey: string };

/** A fact counts as "new this week" if learned within this many days. */
export const NEW_WINDOW_DAYS = 7;

const DAY = 86_400_000;
/** Innermost ring radius (clear of the 96px sun + caption) and the per-ring step.
 *  STEP was 32 when eight rings had to fit; with three subjects they should read
 *  as three deliberate bands rather than a fading stack. 116/188/260, off-lane 332,
 *  whose vertical half-extent (332 * sin60 = 287px) still clears the 820x520 stage. */
const RING_BASE = 116;
const RING_STEP = 72;
/** Degrees between two arcs on a ring, and between two rivals on Competitors. */
const ARC_GAP_DEG = 2.2;
const RIVAL_GAP_DEG = 7;
const DEG = Math.PI / 180;

/** Outer lanes orbit slightly slower, same direction; clamped so deep lanes
 *  never stall or reverse. */
function laneSpeed(lane: number): number {
  return Math.max(0.012, 0.05 - lane * 0.006);
}

/** "BRAND" -> "Brand". Subject labels are stored uppercase for the rail. */
function titleCase(s: string): string {
  return s.charAt(0) + s.slice(1).toLowerCase();
}

/**
 * Resolve the display taxonomy against this graph.
 *
 * This replaced a createdAt sort, which meant ring order was seed-insertion order:
 * an accident of array position in the backend's SYSTEM_CONCEPT_SEEDS, not a
 * decision anyone made.
 */
export function universeSubjects(graph: UniverseGraph): ResolvedSubject[] {
  const concepts = graph.nodes.filter(
    (n): n is Extract<typeof n, { type: "concept" }> => n.type === "concept",
  );
  return resolveTaxonomy(concepts);
}

/** All concepts in TAXONOMY order, so the proposal-tray chips agree with the rail.
 *  Anything the taxonomy does not name sorts last, keeping its old relative order. */
export function listUniverseConcepts(graph: UniverseGraph) {
  const rank = new Map<string, number>();
  universeSubjects(graph).forEach((s) =>
    s.facets.forEach((f) => {
      if (!rank.has(f.conceptId)) rank.set(f.conceptId, rank.size);
    }),
  );
  const LAST = Number.MAX_SAFE_INTEGER;
  return graph.nodes
    .filter((n) => n.type === "concept")
    .slice()
    .sort((a, b) => {
      const ra = rank.get(a.id) ?? LAST;
      const rb = rank.get(b.id) ?? LAST;
      if (ra !== rb) return ra - rb;
      if (a.isSystem !== b.isSystem) return a.isSystem ? -1 : 1;
      return a.name.localeCompare(b.name);
    });
}

/**
 * Compute the orbit rings + the staged fact layout for a graph.
 * `now` defaults to the real clock so the new/established split is live-correct.
 */
export function buildUniverseLayout(
  graph: UniverseGraph,
  now: number = Date.now(),
): { facts: StageFact[]; rings: Ring[]; subjects: ResolvedSubject[] } {
  const allSubjects = universeSubjects(graph);
  const subjects = ringSubjects(allSubjects);
  const rings: Ring[] = subjects.map((s, i) => ({
    id: s.key,
    radius: RING_BASE + i * RING_STEP,
    label: s.label,
    conceptIds: [
      ...(s.anchorConceptId ? [s.anchorConceptId] : []),
      ...s.facets.map((f) => f.conceptId),
    ],
    blurb: s.blurb,
    speed: laneSpeed(i),
    // Filled below, once the facts are known.
    arcs: [],
    echoes: [],
  }));

  // conceptId -> its facet, and the subject lane that facet lives on. A fact is
  // placed by the LOWEST taxonomy rank among its memberships, not by the strongest
  // weight: weight leans on concept creation order, so a re-seed would silently
  // reshuffle every planet with no data change behind it.
  type Placement = { lane: number; facetRank: number; facetKey: string; facetLabel: string; subjectKey: string; subjectLabel: string; byMarker: boolean };

  // Two different jobs, and conflating them is what put every competitor's channels
  // under BRAND > Channels. "TikTok posts 4x a week on Instagram" is tagged BOTH
  // Competitors and Channels. The ANCHOR (Competitors) decides which subject owns
  // the fact; the FACET (Channels) decides which drawer inside it. Brand has no
  // anchor because it is the default: a fact tagged only with facet concepts is
  // about us.
  const anchorLane = new Map<string, number>();
  subjects.forEach((s, lane) => {
    if (s.anchorConceptId) anchorLane.set(s.anchorConceptId, lane);
  });
  // conceptId -> the facet drawer within a GIVEN subject lane.
  const facetIn = new Map<string, Map<string, { rank: number; key: string; label: string }>>();
  subjects.forEach((s) => {
    const m = new Map<string, { rank: number; key: string; label: string }>();
    s.facets.forEach((f, i) => m.set(f.conceptId, { rank: i, key: f.key, label: f.label }));
    facetIn.set(s.key, m);
  });

  // Membership edges: a fact belongs to MANY concepts, not one. Collect them ALL.
  // Collapsing this to a single Map<factId, conceptId> is what made the maturity
  // meter undercount: the last edge in graph.edges silently won and every earlier
  // membership was dropped.
  const membershipsOf = new Map<string, Array<{ conceptId: string; weight: number }>>();
  graph.edges.forEach((e) => {
    if (e.kind !== "membership") return;
    const list = membershipsOf.get(e.sourceId);
    const entry = { conceptId: e.targetId, weight: e.weight ?? 0 };
    if (list) list.push(entry);
    else membershipsOf.set(e.sourceId, [entry]);
  });

  const facts = graph.nodes.filter((n): n is UniverseFactNode => n.type === "fact");
  // `?? []` keeps this build safe during the short backend/frontend rollout
  // window; the landed contract always includes the array.
  const entityById = new Map((graph.entities ?? []).map((entity) => [entity.id, entity]));
  const laneCount = Math.max(1, rings.length);

  // A planet has ONE position, so each fact still gets one display lane — but the
  // choice is now deterministic: strongest membership wins, ties broken by ring
  // order. It used to be "last edge wins", and the backend's memberships query has
  // no ORDER BY, so a multi-concept planet visibly hopped orbits between polls with
  // no data change behind it.
  const chosen = facts.map((f): Placement | null => {
    const cids = (membershipsOf.get(f.id) ?? []).map((m) => m.conceptId);

    // 1. Subject. A named entity is the strongest available signal because it
    // is an explicit independent axis, not a classifier guess. This also keeps a
    // competitor campaign on COMPETITORS even when its only facet is the shared
    // Campaigns concept. During rollout (no entity yet), the existing anchor
    // routing below remains unchanged.
    let lane = -1;
    const entity = f.entityId ? entityById.get(f.entityId) : undefined;
    // Deleted rivals keep their stored name/id snapshot on old facts. Those
    // facts are still competitor knowledge even though the entity row no longer
    // resolves, so the presence of either attribution field is a valid gate.
    const entitySubjectKey =
      entity?.kind === "competitor" || f.entityId || f.entityName
        ? "competitors"
        : null;
    if (entitySubjectKey) lane = subjects.findIndex((s) => s.key === entitySubjectKey);
    let byMarker = lane !== -1;

    // 2. Without a resolved entity, an anchor wins outright; ties go to the
    // earlier subject.
    if (lane === -1) {
      for (const cid of cids) {
        const anchoredLane = anchorLane.get(cid);
        if (anchoredLane !== undefined && (lane === -1 || anchoredLane < lane)) {
          lane = anchoredLane;
        }
      }
      byMarker = lane !== -1;
    }
    // 3. No anchor: the fact is about the DEFAULT subject (us), if any of its
    //    concepts is one of that subject's facets.
    //
    //    This used to take "the first subject whose facets match", which happened
    //    to be BRAND only because BRAND was first in the list that had concepts.
    //    The moment CATEGORY is seeded it sits first in the owner's order and would
    //    hijack every untagged brand fact. The default is named, not positional.
    if (lane === -1) {
      const di = subjects.findIndex((s) => s.key === DEFAULT_SUBJECT_KEY);
      if (di >= 0 && cids.some((cid) => facetIn.get(subjects[di].key)?.has(cid))) {
        lane = di;
      }
    }
    // 4. Still nothing: a concept that belongs to some other subject's facets but
    //    carries no marker. Fall through to that subject rather than dropping the
    //    fact off the rings entirely.
    if (lane === -1) {
      for (let i = 0; i < subjects.length && lane === -1; i++) {
        if (cids.some((cid) => facetIn.get(subjects[i].key)?.has(cid))) lane = i;
      }
    }
    if (lane === -1) return null;

    // 5. Drawer inside that subject.
    const subject = subjects[lane];
    const drawers = facetIn.get(subject.key);
    let best: { rank: number; key: string; label: string } | null = null;
    for (const cid of cids) {
      const d = drawers?.get(cid);
      if (d && (!best || d.rank < best.rank)) best = d;
    }
    return {
      lane,
      facetRank: best?.rank ?? 0,
      facetKey: best?.key ?? `${subject.key}.general`,
      facetLabel: best?.label ?? "General",
      subjectKey: subject.key,
      subjectLabel: subject.label,
      byMarker,
    };
  });
  const ringOf = chosen.map((p) => (p ? p.lane : -1));

  // Cross-subject filings. A fact the default or facet rule placed can also be
  // filed in another subject by a concept only that subject owns: "Ramadan
  // bookmarking" is Audience behaviour AND a Culture moment. It lists under both
  // and echoes on the second ring. Never into or out of a marker subject
  // (Competitors): the anchor or entity decides that fact's subject outright,
  // which is what keeps a rival's channels out of BRAND > Channels.
  const crossOf = facts.map((f, i): string[] => {
    const place = chosen[i];
    if (!place || place.byMarker) return [];
    const cids = (membershipsOf.get(f.id) ?? []).map((m) => m.conceptId);
    const own = facetIn.get(place.subjectKey);
    const out: string[] = [];
    for (const s of subjects) {
      if (s.key === place.subjectKey || s.anchorConceptId || s.key === OTHER_SUBJECT_KEY) continue;
      const theirs = facetIn.get(s.key);
      if (cids.some((cid) => theirs?.has(cid) && !own?.has(cid))) out.push(s.key);
    }
    return out;
  });

  // Off-ring facts (no membership at all, or membership only on concepts that have
  // no orbit) ride a lane just outside the outermost orbit, so they read as what
  // they are: not in any named orbit. They must NEVER index perLane/seen with -1:
  // that yields NaN angles and the planet disappears from the stage entirely.
  const OFF_LANE = laneCount;
  const laneOf = ringOf.map((r) => (r >= 0 ? r : OFF_LANE));
  const perLane = new Array(laneCount + 1).fill(0);
  laneOf.forEach((l) => { perLane[l] += 1; });
  const seen = new Array(laneCount + 1).fill(0);
  const OFF_RADIUS = RING_BASE + rings.length * RING_STEP;

  const stageFacts: StageFact[] = facts.map((f, i) => {
    const ring = ringOf[i];
    const lane = laneOf[i];
    const place = chosen[i];
    const learnedAt = f.createdAt;
    const entity = f.entityId ? entityById.get(f.entityId) : undefined;
    const entityName = (entity?.name ?? f.entityName ?? "").trim();
    const ageDays = (now - Date.parse(learnedAt)) / DAY;
    const isNew = Number.isFinite(ageDays) && ageDays <= NEW_WINDOW_DAYS;
    // Off-lane facts keep today's even slot on their lane (+ a per-lane phase).
    // Facts on a ring are placed inside their group's arc below.
    const k = seen[lane]++;
    const ang0 = (k / Math.max(1, perLane[lane])) * 6.2832 + lane * 0.6;
    return {
      id: f.id,
      kind: isNew ? "new" : "established",
      label: f.name,
      concept: place?.facetLabel ?? "",
      facetKey: place?.facetKey ?? "",
      subjectKey: place ? place.subjectKey : UNSORTED_KEY,
      entityId: f.entityId ?? entity?.id ?? null,
      entityName,
      path: place
        ? [titleCase(place.subjectLabel), entityName, place.facetLabel].filter(Boolean).join(" · ")
        : "",
      conceptIds: (membershipsOf.get(f.id) ?? []).map((m) => m.conceptId),
      desc: f.description ?? "",
      learnedAt,
      updatedAt: f.updatedAt ?? learnedAt,
      groupKey: "",
      crossSubjectKeys: crossOf[i],
      ring,
      ang0,
      R: ring >= 0 ? (rings[ring]?.radius ?? RING_BASE) : OFF_RADIUS,
      speed: laneSpeed(lane),
      yoff: 0,
    };
  });

  // Each ring is split into ARCS, one per group, taken from the same outline the
  // list renders, so an arc, a list group and the fact card's group are one
  // thing. A fact sits in its PRIMARY group's arc (the first group in list order
  // that holds it, which is the facet it is placed under); any other group of the
  // same subject that also holds it gets an echo.
  const outline = buildUniverseOutline(graph, stageFacts, allSubjects);
  const factIndex = new Map(stageFacts.map((f, i) => [f.id, i]));
  rings.forEach((ring, ringIdx) => {
    const group = outline.find((g) => g.subjectKey === ring.id);
    const drafts = group ? arcDrafts(group, subjects[ringIdx]) : [];
    if (!drafts.length) return;
    const placed = placeArcs(drafts, ringIdx);
    ring.arcs = placed;
    for (const arc of placed) {
      const n = arc.factIds.length;
      arc.factIds.forEach((id, j) => {
        const fi = factIndex.get(id);
        if (fi == null) return;
        const fact = stageFacts[fi];
        const ang0 = arc.start + ((arc.end - arc.start) * (j + 0.5)) / n;
        if (!fact.groupKey && fact.ring === ringIdx) {
          fact.groupKey = arc.key;
          fact.ang0 = ang0;
          fact.path = [titleCase(ring.label), arc.entityName, arc.label].filter(Boolean).join(" · ");
        } else {
          // A second filing, in this subject or (cross-filed) in another one.
          ring.echoes.push({ factId: id, ang0, arcKey: arc.key });
        }
      });
    }
  });

  return { facts: stageFacts, rings, subjects: allSubjects };
}

type ArcDraft = Omit<RingArc, "start" | "end"> & {
  /** Relative sweep. */
  weight: number;
  /** Degrees of gap after this arc. */
  gapAfter: number;
};

/** The drawable groups of one subject, in list order. Planned groups (no concept
 *  for this brand) are neither drawn nor counted. */
function arcDrafts(group: UniverseOutlineGroup, subject: ResolvedSubject): ArcDraft[] {
  const subjectKey = subject.key;
  const children = group.children ?? [];
  const drafts: ArcDraft[] = [];
  // Outline facet keys end in ":facet:<facet key>"; the facet names the concept.
  const conceptByFacet = new Map(subject.facets.map((f) => [f.key, f.concept]));
  const conceptOf = (key: string) => conceptByFacet.get(key.split(":facet:")[1] ?? "");
  const rivalMode = children.some((c) => c.kind === "entity" || c.kind === "general");
  if (rivalMode) {
    // One arc per rival per rival facet, every one the same size. The
    // Across-the-set bucket (competitor facts with no rival yet) is not a rival:
    // only its filled groups are drawn, and none of it counts as a gap.
    for (const bucket of children) {
      const isRival = bucket.kind === "entity";
      const kids = (bucket.children ?? []).filter((c) => !c.planned && (isRival || c.facts.length > 0));
      kids.forEach((c, j) => {
        drafts.push({
          key: c.key,
          label: c.label,
          subjectKey,
          entityKey: bucket.key,
          entityName: bucket.label,
          concept: conceptOf(c.key),
          charted: c.facts.length > 0,
          countable: isRival,
          factIds: c.facts.map((f) => f.id),
          weight: 1,
          gapAfter: j === kids.length - 1 ? RIVAL_GAP_DEG : ARC_GAP_DEG,
        });
      });
    }
    return drafts;
  }
  for (const c of children) {
    if (c.planned) continue;
    drafts.push({
      key: c.key,
      label: c.label,
      subjectKey,
      concept: conceptOf(c.key),
      charted: c.facts.length > 0,
      countable: true,
      factIds: c.facts.map((f) => f.id),
      weight: c.facts.length + 1,
      gapAfter: ARC_GAP_DEG,
    });
  }
  // Facts in the subject with no facet sit directly on the subject row in the
  // list. They still need a span on the ring, drawn only while it holds facts.
  if (group.facts.length) {
    drafts.push({
      key: `${group.key}:general`,
      label: "General",
      subjectKey,
      charted: true,
      countable: false,
      factIds: group.facts.map((f) => f.id),
      weight: group.facts.length + 1,
      gapAfter: ARC_GAP_DEG,
    });
  }
  return drafts;
}

/** Sweep each arc in proportion to its weight, after the gaps, from this ring's
 *  own start phase so boundaries never line up radially across rings. */
function placeArcs(drafts: ArcDraft[], ringIdx: number): RingArc[] {
  let gapTotal = drafts.reduce((sum, d) => sum + d.gapAfter, 0);
  // A very long roster could eat the whole circle in gaps; cap them at half.
  const gapScale = gapTotal > 180 ? 180 / gapTotal : 1;
  gapTotal *= gapScale;
  const available = 360 - gapTotal;
  const weightTotal = drafts.reduce((sum, d) => sum + d.weight, 0) || 1;
  let cursor = ringIdx * 37 + 100;
  return drafts.map(({ weight, gapAfter, ...arc }) => {
    const sweep = (available * weight) / weightTotal;
    const start = cursor;
    const end = cursor + sweep;
    cursor = end + gapAfter * gapScale;
    return { ...arc, start: start * DEG, end: end * DEG };
  });
}


/**
 * Frontend-only maturity model: the universe is mature at TARGET_TOTAL_FACTS
 * accepted facts in total, wherever they sit. Per-subject and per-facet
 * coverage is still derived — not for the score, but for the grow CTAs, which
 * keep steering new facts toward empty and thin orbits. No backend field; pure
 * derivation from the staged layout.
 */
export const TARGET_TOTAL_FACTS = 24;

export type UniverseMaturityTier = "forming" | "growing" | "shaping" | "mature";

export type FacetCoverage = {
  key: string;
  label: string;
  /** The backend concept name. What the grow prompt must say, never `label`. */
  concept: string;
  count: number;
};

export type ConceptCoverage = {
  ring: number;
  /** Taxonomy subject key. */
  key: string;
  label: string;
  /** Distinct facts across this subject's facets (a fact on two facets counts once). */
  count: number;
  facets: FacetCoverage[];
};

export type UniverseMaturity = {
  concepts: ConceptCoverage[];
  /** Total accepted facts, off-ring included — the number maturity scores. */
  total: number;
  /** TARGET_TOTAL_FACTS unless overridden. */
  target: number;
  /** 0..1 overall progress. */
  progress: number;
  tier: UniverseMaturityTier;
  /** Subjects, empties first then thinnest — for CTAs. Empty once the universe is mature. */
  nextUp: ConceptCoverage[];
  /**
   * FACETS, empties first then thinnest. Carries the backend CONCEPT NAME, which
   * is what the grow prompt must say: the agent files facts by concept name, so a
   * display label like "Voice & tone" routes nowhere. Planned subjects (Category,
   * Culture) never appear here because they have no concept to file into —
   * offering them would produce proposals that cannot be stored. Empty once the
   * universe is mature.
   */
  growTargets: FacetCoverage[];
  /**
   * Facts no orbit owns (zero memberships, or memberships only on concepts with no
   * orbit). They count toward the total but toward no orbit's coverage. Surface
   * them rather than folding them into a concept: the backend supports conceptless
   * facts explicitly (ProposeFact takes `conceptIds: []`, "Pass [] if none fit"),
   * and they used to be credited silently to the oldest orbit.
   */
  offRing: number;
};

export function computeUniverseMaturity(
  facts: StageFact[],
  rings: Ring[],
  subjects: ResolvedSubject[] = [],
  target: number = TARGET_TOTAL_FACTS,
): UniverseMaturity {
  const subjectFacets = new Map(subjects.map((s) => [s.key, s.facets]));
  // Count against the subject's CONCEPT IDS, scoped to facts PLACED in this
  // subject. `f.conceptIds.includes(ring.id)` is always false now — ring.id is a
  // subject key — and would zero every count; counting raw membership instead
  // would put every competitor's channel fact in BRAND > Channels as well as its own.
  const concepts: ConceptCoverage[] = rings.map((ring, i) => {
    const facetsOf = subjectFacets.get(ring.id) ?? [];
    const facets: FacetCoverage[] = facetsOf.map((f) => ({
      key: f.key,
      label: f.label,
      concept: f.concept,
      count: facts.filter(
        (sf) => sf.subjectKey === ring.id && sf.conceptIds.includes(f.conceptId),
      ).length,
    }));
    // Distinct facts, so a fact on Voice AND Channels counts once for BRAND.
    const count = facts.filter((sf) => sf.subjectKey === ring.id).length;
    return { ring: i, key: ring.id, label: ring.label, count, facets };
  });
  const offRing = facts.filter((f) => f.ring < 0).length;
  const total = facts.length;
  const progress = target > 0 ? Math.min(1, total / target) : 1;

  let tier: UniverseMaturityTier;
  if (total === 0) tier = "forming";
  else if (progress < 0.5) tier = "growing";
  else if (progress < 1) tier = "shaping";
  else tier = "mature";

  const nextUp =
    tier === "mature"
      ? []
      : [...concepts].sort((a, b) => a.count - b.count || a.ring - b.ring);

  const growTargets =
    tier === "mature"
      ? []
      : concepts.flatMap((c) => c.facets).sort((a, b) => a.count - b.count);

  return { concepts, total, target, progress, tier, nextUp, growTargets, offRing };
}

/** Short status line for the maturity meter. */
export function maturityLabel(m: UniverseMaturity): string {
  const remaining = m.target - m.total;
  switch (m.tier) {
    case "forming":
      return "Your universe is forming";
    case "growing":
      return `${m.total} of ${m.target} facts gathered`;
    case "shaping":
      // No em dash: this string is user-facing copy on two surfaces now.
      return `Taking shape, ${remaining} fact${remaining === 1 ? "" : "s"} to go`;
    case "mature":
      return "Universe is mature";
  }
}

/** Prefill prompt that asks the agents to grow the weakest concepts. */
export function maturityGrowPrompt(m: UniverseMaturity): string {
  if (m.tier === "mature") {
    return "Propose 15–20 fresh, non-duplicate brand-universe facts that deepen existing orbits. Complete the full batch, then wait for my approval.";
  }
  // Concept NAMES, never display labels. See growTargets.
  const focus = m.growTargets.slice(0, 3).map((f) => f.concept);
  if (m.tier === "forming") {
    return `My brand universe is empty. Enable the Brand Universe Setup skill, research what you can verify, and propose 15–20 foundational facts across the core orbits (${focus.join(", ") || "all concepts"}). Complete the full batch, then wait for my approval.`;
  }
  const empty = m.growTargets.filter((f) => f.count === 0).map((f) => f.concept);
  if (empty.length) {
    return `Propose 15–20 non-duplicate brand-universe facts, prioritizing these empty orbits: ${empty.slice(0, 4).join(", ")}. Then deepen the thinnest remaining orbits. Complete the full batch, then wait for my approval.`;
  }
  return `Propose 15–20 non-duplicate brand-universe facts, prioritizing these thin orbits: ${focus.join(", ")}. Complete the full batch, then wait for my approval.`;
}
